import { Component, OnInit, OnDestroy, Optional, Inject, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { I18NextPipe } from 'angular-i18next';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { QueueItem, AgentItem, ColumnItem, LogoItem, WallboardSettingsUpdateRequest, WallboardSettingsResponse } from './models';
import { realTimeService } from '../../../services/services/realtime.service';
import { DialogHelpComponent } from 'src/app/common/dialogs/dialog-help/dialog-help.component';
import { IHelpDialogData } from 'src/app/models';

@Component({
    selector: 'app-wallboard-settings',
    templateUrl: './wallboard-settings.component.html',
    styleUrls: ['./wallboard-settings.component.scss'],
    standalone: false
})
export class WallboardSettingsComponent implements OnInit, OnDestroy {
  @Output() close = new EventEmitter<void>();
  @Output() settingsSaved = new EventEmitter<void>();

  // Model data
  model: WallboardSettingsResponse = {
    ok: false,
    wallboardType: 'Queue',
    switchTime: 15,
    queues: [],
    columns: [],
    selectedQueues: [],
    checkInQueueAgent: false,
    agents: [],
    agentColumns: [],
    checkLogOnAgent: false,
    activeAgentSave: true,
    wallboardBackgroundColor: '#d41919',
    wallboardTextColor: '#f0afaf',
    wallboardFontFamily: 'Poppins',
    wallboardLogo: '',
    logoId: 0,
    logoItems: [],
    footerText: '',
    maxRows: 10,
    editAsUserId: null,
    editAsUserName: ''
  };

  // Component state
  accordionStates = {
    queue: false,
    agent: false,
    display: false
  };

  // Form data
  selectedQueues: number[] = [];
  selectedColumns: number[] = [];
  selectedAgents: number[] = [];
  selectedAgentColumns: number[] = [];

  // Sorted lists for display
  sortedQueues: QueueItem[] = [];
  sortedColumns: ColumnItem[] = [];
  sortedAgents: AgentItem[] = [];
  sortedAgentColumns: ColumnItem[] = [];

  fontOptions = [
    { value: 'Poppins', label: 'Poppins' },
    { value: 'Open sans', label: 'Open sans' },
    { value: 'Arial', label: 'Arial' },
    { value: 'Calibri', label: 'Calibri' },
    { value: 'Georgia', label: 'Georgia' },
    { value: 'serif', label: 'Serif' },
    { value: 'tahoma', label: 'Tahoma' },
    { value: 'Times New Roman', label: 'Times New Roman' },
    { value: 'Trebuchet MS', label: 'Trebuchet MS' },
    { value: 'Verdana', label: 'Verdana' }
  ];

  // Color selection
  predefinedBackgroundColors = ['#d41919', '#e06710', '#56802d', '#0492d4', '#0f4999', '#73716c'];
  predefinedTextColors = ['#f0afaf', '#f4caac', '#c4d3b6', '#a7d9f0', '#abbfdb', '#cecdcc'];
  customBackgroundColor = '#C2C2C2';
  customTextColor = '#C2C2C2';

  // Additional color schemes for different themes
  colorSchemes = {
    corporate: {
      background: '#0f4999',
      text: '#abbfdb',
      name: 'Corporate Blue'
    },
    emergency: {
      background: '#d41919',
      text: '#f0afaf',
      name: 'Emergency Red'
    },
    success: {
      background: '#56802d',
      text: '#c4d3b6',
      name: 'Success Green'
    },
    warning: {
      background: '#e06710',
      text: '#f4caac',
      name: 'Warning Orange'
    },
    neutral: {
      background: '#73716c',
      text: '#cecdcc',
      name: 'Neutral Gray'
    },
    custom: {
      background: '#C2C2C2',
      text: '#C2C2C2',
      name: 'Custom'
    }
  };

  // Change tracking
  hasWallboardTypeChanges = false;
  hasQueueChanges = false;
  hasAgentChanges = false;
  hasDisplayChanges = false;

  // Error states
  switchError = '';
  queueError = '';
  agentError = '';
  displayError = '';
  footerTextError = '';
  maxRowsError = '';

  // Snapshot data for undo functionality
  private wallboardTypeSnapshot: any = {};
  private queueSnapshot: any = {};
  private agentSnapshot: any = {};
  private displaySnapshot: any = {};

  // Breadcrumbs
  BreadcrumbsLink = [
    { "label": "Home", "url": "/" },
    { "label": "Real-Time", "url": "/real-time/overview" },
    { "label": "Wallboard", "url": "/real-time/wallboard" },
    { "label": "Wallboard Settings", "url": "/real-time/wallboard/settings" }];

  constructor(
    private fb: FormBuilder,
    private realTimeService: realTimeService,
    private dialog: MatDialog,
    private i18nextPipe: I18NextPipe,
    @Optional() public dialogRef?: MatDialogRef<WallboardSettingsComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData?: any
  ) { }

  ngOnInit(): void {
    this.initializeComponent();
    this.loadWallboardSettings();
  }

  ngOnDestroy(): void { }

  private initializeComponent(): void {
    // Initialize accordion states
    this.accordionStates.queue = false;
    this.accordionStates.agent = false;
    this.accordionStates.display = false;

    // Initialize color pickers
    this.customBackgroundColor = this.model.wallboardBackgroundColor || '#d41919';
    this.customTextColor = this.model.wallboardTextColor || '#f0afaf';

    // Take initial snapshots
    this.takeWallboardTypeSnapshot();
    this.takeQueueSnapshot();
    this.takeAgentSnapshot();
    this.takeDisplaySnapshot();
  }

  private loadWallboardSettings(): void {
    this.realTimeService.GetWallboardSettings().subscribe({
      next: (response: WallboardSettingsResponse) => {
        this.model = { ...this.model, ...response };

        // Initialize color pickers
        this.customBackgroundColor = this.model.wallboardBackgroundColor || '#d41919';
        this.customTextColor = this.model.wallboardTextColor || '#f0afaf';

        // Initialize selections from the response
        // For queues, we need to determine which ones are selected based on the wallboard type
        this.selectedQueues = this.model.selectedQueues?.map(queue => queue) || [];

        // For columns, convert checkedColumns (string names) to column IDs
        this.selectedColumns = this.model.checkedColumns?.map(columnName => {
          const column = this.model.columns?.find(col => col.item2 === columnName);
          return column ? column.item1 : null;
        }).filter(id => id !== null) || [];

        // For agents, we need to determine which ones are selected based on the wallboard type
        this.selectedAgents = this.model.selectedAgents?.map(agent => agent) || [];

        // For agent columns, convert checkedAgentColumns (string names) to column IDs
        this.selectedAgentColumns = this.model.checkedAgentColumns?.map(columnName => {
          const column = this.model.agentColumns?.find(col => col.item2 === columnName);
          return column ? column.item1 : null;
        }).filter(id => id !== null) || [];

        // Update sorted lists
        this.updateSortedLists();

        // Take snapshots after loading
        this.takeWallboardTypeSnapshot();
        this.takeQueueSnapshot();
        this.takeAgentSnapshot();
        this.takeDisplaySnapshot();

        // Update button states to reflect loaded data
        this.updateWallboardTypeButtonsState();
        this.updateQueueButtonsState();
        this.updateAgentButtonsState();
        this.updateDisplayButtonsState();

        this.onDisplaySettingsChange();
      },
      error: (error) => {
        this.handleApiError(this.i18nextPipe.transform('realtime:shared_err_genericServerError'));
        console.error('Error loading wallboard settings:', error);
      }
    });
  }


  private handleApiError(message: string): void {
    console.error('API Error:', message);
    // You could show a toast notification or error message to the user here
    // Consider showing a user-friendly error message in the UI
  }

  // Drag and drop event handlers
  onQueueDrop(event: CdkDragDrop<QueueItem[]>): void {
    if (event.previousIndex === event.currentIndex) return;

    console.log('Queue drop event:', {
      previousIndex: event.previousIndex,
      currentIndex: event.currentIndex,
      item: this.sortedQueues[event.previousIndex]
    });

    moveItemInArray(this.sortedQueues, event.previousIndex, event.currentIndex);

    // Update the selectedQueues array to match the new order
    this.selectedQueues = this.sortedQueues.map(queue => queue.id);

    this.updateQueueButtonsState();
  }

  onColumnDrop(event: CdkDragDrop<ColumnItem[]>): void {
    if (event.previousIndex === event.currentIndex) return;

    console.log('Column drop event:', {
      previousIndex: event.previousIndex,
      currentIndex: event.currentIndex,
      item: this.sortedColumns[event.previousIndex]
    });

    moveItemInArray(this.sortedColumns, event.previousIndex, event.currentIndex);

    // Update the selectedColumns array to match the new order
    this.selectedColumns = this.sortedColumns.map(column => column.item1);

    this.updateQueueButtonsState();
  }

  onAgentDrop(event: CdkDragDrop<AgentItem[]>): void {
    if (event.previousIndex === event.currentIndex) return;

    console.log('Agent drop event:', {
      previousIndex: event.previousIndex,
      currentIndex: event.currentIndex,
      item: this.sortedAgents[event.previousIndex]
    });

    moveItemInArray(this.sortedAgents, event.previousIndex, event.currentIndex);

    // Update the selectedAgents array to match the new order
    this.selectedAgents = this.sortedAgents.map(agent => agent.userId);

    this.updateAgentButtonsState();
  }

  onAgentColumnDrop(event: CdkDragDrop<ColumnItem[]>): void {
    if (event.previousIndex === event.currentIndex) return;

    console.log('Agent column drop event:', {
      previousIndex: event.previousIndex,
      currentIndex: event.currentIndex,
      item: this.sortedAgentColumns[event.previousIndex]
    });

    moveItemInArray(this.sortedAgentColumns, event.previousIndex, event.currentIndex);

    // Update the selectedAgentColumns array to match the new order
    this.selectedAgentColumns = this.sortedAgentColumns.map(column => column.item1);

    this.updateAgentButtonsState();
  }

  // Help dialog methods
  openHelp(type: string): void {
    const helpKeys = {
      main: {
        title: 'realtime:help_title_wallboardSettings',
        text: 'realtime:help_wallboardSettingsMain'
      },
      queue: {
        title: 'realtime:help_title_wallboardQueueSettings',
        text: 'realtime:help_wallboardQueueSettings'
      },
      agent: {
        title: 'realtime:help_title_wallboardAgentSettings',
        text: 'realtime:help_wallboardAgentSettings'
      },
      display: {
        title: 'realtime:help_title_wallboardDisplaySettings',
        text: 'realtime:help_wallboardDisplaySettings'
      }
    };

    const helpData = helpKeys[type] || helpKeys.main;

    this.dialog.open(DialogHelpComponent, {
      panelClass: 'dialog-help-pannel',
      backdropClass: 'backdrop-invisible',
      width: '600px',
      data: {
        title: this.i18nextPipe.transform(helpData.title),
        text: this.i18nextPipe.transform(helpData.text),
        renderAsHtml: true
      } as IHelpDialogData
    });
  }

  // Accordion methods
  toggleAccordion(type: 'queue' | 'agent' | 'display'): void {
    this.accordionStates[type] = !this.accordionStates[type];
  }

  // Wallboard type methods
  onWallboardTypeChange(): void {
    setTimeout(() => { this.updateWallboardTypeButtonsState(); }, 0);

  }

  onSwitchTimeChange(): void {
    this.validateSwitchTime();
    this.updateWallboardTypeButtonsState();
  }

  private validateSwitchTime(): void {
    const switchTime = Number(this.model.switchTime);
    if (isNaN(switchTime) || (switchTime !== 5 && (switchTime < 10 || switchTime > 30))) {
      this.switchError = this.i18nextPipe.transform('realtime:wallboard_err_invalidValueFont');
    } else {
      this.switchError = '';
    }
  }



  private updateWallboardTypeButtonsState(): void {
    this.hasWallboardTypeChanges = this.checkWallboardTypeChanges();
  }

  private checkWallboardTypeChanges(): boolean {
    return this.model.wallboardType !== this.wallboardTypeSnapshot.wallboardType ||
      this.model.switchTime !== this.wallboardTypeSnapshot.switchTime;
  }

  saveWallboardTypeSettings(): void {
    if (!this.hasWallboardTypeChanges) return;

    const request: WallboardSettingsUpdateRequest = {
      wallboardType: this.model.wallboardType,
      switchTime: this.model.switchTime
    };

    // Call service to save
    this.realTimeService.SaveWallboardSettings(request).subscribe(() => {
      this.takeWallboardTypeSnapshot();
      this.updateWallboardTypeButtonsState();
      this.showSuccessMessage(this.i18nextPipe.transform('realtime:shared_err_ok'));
      this.notifyParentOfSave();
    });
  }

  undoWallboardTypeChanges(): void {
    this.model.wallboardType = this.wallboardTypeSnapshot.wallboardType;
    this.model.switchTime = this.wallboardTypeSnapshot.switchTime;
    this.updateWallboardTypeButtonsState();
    this.switchError = '';
  }

  private takeWallboardTypeSnapshot(): void {
    this.wallboardTypeSnapshot = {
      wallboardType: this.model.wallboardType,
      switchTime: this.model.switchTime
    };
  }

  // Queue methods
  onQueueSelectionChange(): void {
    this.updateSortedQueues();
    this.updateQueueButtonsState();
  }

  onColumnSelectionChange(): void {
    this.updateSortedColumns();
    this.updateQueueButtonsState();
  }

  onQueueSettingsChange(): void {
    this.updateQueueButtonsState();
  }

  private updateSortedQueues(): void {
    this.sortedQueues = this.model.queues.filter(queue =>
      this.selectedQueues.includes(queue.id)
    );
  }

  private updateSortedColumns(): void {
    this.sortedColumns = this.model.columns.filter(column =>
      this.selectedColumns.includes(column.item1)
    );
  }

  private updateQueueButtonsState(): void {
    this.hasQueueChanges = this.checkQueueChanges();
  }

  private checkQueueChanges(): boolean {
    const queueValidation = this.validateQueueSelection();
    if (queueValidation.failed) return false;

    return this.selectedQueues.length !== this.queueSnapshot.selectedQueues.length ||
      this.selectedColumns.length !== this.queueSnapshot.selectedColumns.length ||
      this.model.checkInQueueAgent !== this.queueSnapshot.checkInQueueAgent ||
      this.hasQueueSequenceChanged() ||
      this.hasColumnSequenceChanged();
  }

  private validateQueueSelection(): { failed: boolean, message?: string } {
    if (this.selectedQueues.length === 0 || this.selectedColumns.length === 0) {
      return { failed: true, message: this.i18nextPipe.transform('realtime:wallboard_err_selectOneQueue') };
    }

    if (this.selectedQueues.length === 1 && this.selectedColumns.length !== 6) {
      return { failed: true, message: this.i18nextPipe.transform('realtime:wallboard_lang_Select6') };
    }

    return { failed: false };
  }

  private hasQueueSequenceChanged(): boolean {
    if (!this.queueSnapshot.sortedQueues || this.queueSnapshot.sortedQueues.length !== this.sortedQueues.length) {
      return true;
    }

    return this.sortedQueues.some((queue, index) =>
      queue.id !== this.queueSnapshot.sortedQueues[index]?.id
    );
  }

  private hasColumnSequenceChanged(): boolean {
    if (!this.queueSnapshot.sortedColumns || this.queueSnapshot.sortedColumns.length !== this.sortedColumns.length) {
      return true;
    }

    return this.sortedColumns.some((column, index) =>
      column.item1 !== this.queueSnapshot.sortedColumns[index]?.item1
    );
  }

  saveQueueSettings(): void {
    if (!this.hasQueueChanges) return;

    const validation = this.validateQueueSelection();
    if (validation.failed) {
      this.queueError = validation.message || this.i18nextPipe.transform('realtime:wallboard_err_selectOneQueue');
      return;
    }

    const request: WallboardSettingsUpdateRequest = {
      queues: this.selectedQueues,
      queueColumns: this.selectedColumns,
      checkInQueueAgent: this.model.checkInQueueAgent.toString()
    };

    this.realTimeService.SaveWallboardSettings(request).subscribe(() => {
      this.takeQueueSnapshot();
      this.updateQueueButtonsState();
      this.showSuccessMessage(this.i18nextPipe.transform('realtime:shared_err_ok'));
      this.notifyParentOfSave();
    });
  }

  undoQueueChanges(): void {
    this.selectedQueues = [...this.queueSnapshot.selectedQueues];
    this.selectedColumns = [...this.queueSnapshot.selectedColumns];
    this.model.checkInQueueAgent = this.queueSnapshot.checkInQueueAgent;
    this.updateSortedLists();
    this.updateQueueButtonsState();
    this.queueError = '';
  }

  private takeQueueSnapshot(): void {
    this.queueSnapshot = {
      selectedQueues: [...this.selectedQueues],
      selectedColumns: [...this.selectedColumns],
      checkInQueueAgent: this.model.checkInQueueAgent,
      sortedQueues: [...this.sortedQueues],
      sortedColumns: [...this.sortedColumns]
    };
  }

  // Agent methods
  onAgentSelectionChange(): void {
    this.updateSortedAgents();
    this.updateAgentButtonsState();
  }

  onAgentColumnSelectionChange(): void {
    this.updateSortedAgentColumns();
    this.updateAgentButtonsState();
  }

  onAgentSettingsChange(): void {
    this.updateAgentButtonsState();
  }

  private updateSortedAgents(): void {
    this.sortedAgents = this.model.agents.filter(agent =>
      this.selectedAgents.includes(agent.userId)
    );
  }

  private updateSortedAgentColumns(): void {
    this.sortedAgentColumns = this.model.agentColumns.filter(column =>
      this.selectedAgentColumns.includes(column.item1)
    );
  }

  private updateAgentButtonsState(): void {
    this.hasAgentChanges = this.checkAgentChanges();
  }

  private checkAgentChanges(): boolean {
    if (this.selectedAgents.length === 0 || this.selectedAgentColumns.length === 0) {
      return false;
    }

    return this.selectedAgents.length !== this.agentSnapshot.selectedAgents.length ||
      this.selectedAgentColumns.length !== this.agentSnapshot.selectedAgentColumns.length ||
      this.model.checkLogOnAgent !== this.agentSnapshot.checkLogOnAgent ||
      this.hasAgentSequenceChanged() ||
      this.hasAgentColumnSequenceChanged();
  }

  private hasAgentSequenceChanged(): boolean {
    if (!this.agentSnapshot.sortedAgents || this.agentSnapshot.sortedAgents.length !== this.sortedAgents.length) {
      return true;
    }

    return this.sortedAgents.some((agent, index) =>
      agent.userId !== this.agentSnapshot.sortedAgents[index]?.userId
    );
  }

  private hasAgentColumnSequenceChanged(): boolean {
    if (!this.agentSnapshot.sortedAgentColumns || this.agentSnapshot.sortedAgentColumns.length !== this.sortedAgentColumns.length) {
      return true;
    }

    return this.sortedAgentColumns.some((column, index) =>
      column.item1 !== this.agentSnapshot.sortedAgentColumns[index]?.item1
    );
  }

  saveAgentSettings(): void {
    if (!this.hasAgentChanges) return;

    const request: WallboardSettingsUpdateRequest = {
      agents: this.selectedAgents,
      agentColumns: this.selectedAgentColumns,
      checkLogOnAgent: this.model.checkLogOnAgent.toString()
    };

    this.realTimeService.SaveWallboardSettings(request).subscribe(() => {
      this.takeAgentSnapshot();
      this.updateAgentButtonsState();
      this.showSuccessMessage(this.i18nextPipe.transform('realtime:shared_err_ok'));
      this.notifyParentOfSave();
    });
  }

  undoAgentChanges(): void {
    this.selectedAgents = [...this.agentSnapshot.selectedAgents];
    this.selectedAgentColumns = [...this.agentSnapshot.selectedAgentColumns];
    this.model.checkLogOnAgent = this.agentSnapshot.checkLogOnAgent;
    this.updateSortedLists();
    this.updateAgentButtonsState();
    this.agentError = '';
  }

  private takeAgentSnapshot(): void {
    this.agentSnapshot = {
      selectedAgents: [...this.selectedAgents],
      selectedAgentColumns: [...this.selectedAgentColumns],
      checkLogOnAgent: this.model.checkLogOnAgent,
      sortedAgents: [...this.sortedAgents],
      sortedAgentColumns: [...this.sortedAgentColumns]
    };
  }

  // Display methods
  onDisplaySettingsChange(): void {
    this.validateDisplaySettings();
    this.updateDisplayButtonsState();
  }

  private validateDisplaySettings(): void {
    // Validate footer text
    if (this.model.footerText && this.model.footerText.length > 50) {
      this.footerTextError = this.i18nextPipe.transform('realtime:wallboard_footertextlength');
    } else {
      this.footerTextError = '';
    }

    // Validate max rows max 25 rows
    if (isNaN(this.model.maxRows) || this.model.maxRows < 2 || this.model.maxRows > 25) {
      this.maxRowsError = this.i18nextPipe.transform('realtime:wallboard_invalidRowNumber').replace('100', '25');
    } else {
      this.maxRowsError = '';
    }
  }

  private updateDisplayButtonsState(): void {
    this.hasDisplayChanges = this.checkDisplayChanges();
  }

  private checkDisplayChanges(): boolean {
    return this.model.logoId !== this.displaySnapshot.logoId ||
      this.model.footerText !== this.displaySnapshot.footerText ||
      this.model.maxRows !== this.displaySnapshot.maxRows ||
      this.model.wallboardBackgroundColor !== this.displaySnapshot.wallboardBackgroundColor ||
      this.model.wallboardTextColor !== this.displaySnapshot.wallboardTextColor ||
      this.model.wallboardFontFamily !== this.displaySnapshot.wallboardFontFamily ||
      this.footerTextError !== '' ||
      this.maxRowsError !== '';
  }

  selectBackgroundColor(color: string): void {
    this.model.wallboardBackgroundColor = color;
    this.onDisplaySettingsChange();
  }

  selectCustomBackgroundColor(): void {
    this.model.wallboardBackgroundColor = this.customBackgroundColor;
    this.onDisplaySettingsChange();
  }

  selectTextColor(color: string): void {
    this.model.wallboardTextColor = color;
    this.onDisplaySettingsChange();
  }

  selectCustomTextColor(): void {
    this.model.wallboardTextColor = this.customTextColor;
    this.onDisplaySettingsChange();
  }

  isCustomBackgroundColor(): boolean {
    return !this.predefinedBackgroundColors.includes(this.model.wallboardBackgroundColor);
  }

  isCustomTextColor(): boolean {
    return !this.predefinedTextColors.includes(this.model.wallboardTextColor);
  }

  saveDisplaySettings(): void {
    if (!this.hasDisplayChanges) return;

    const request: WallboardSettingsUpdateRequest = {
      logoId: this.model.logoId,
      footerText: this.model.footerText,
      maxRows: this.model.maxRows.toString(),
      backgroundColor: this.model.wallboardBackgroundColor,
      textColor: this.model.wallboardTextColor,
      fontFamily: this.model.wallboardFontFamily
    };

    this.realTimeService.SaveWallboardSettings(request).subscribe(() => {
      this.takeDisplaySnapshot();
      this.updateDisplayButtonsState();
      this.showSuccessMessage(this.i18nextPipe.transform('realtime:shared_err_ok'));
      this.notifyParentOfSave();
    });
  }

  undoDisplayChanges(): void {
    this.model.logoId = this.displaySnapshot.logoId;
    this.model.footerText = this.displaySnapshot.footerText;
    this.model.maxRows = this.displaySnapshot.maxRows;
    this.model.wallboardBackgroundColor = this.displaySnapshot.wallboardBackgroundColor;
    this.model.wallboardTextColor = this.displaySnapshot.wallboardTextColor;
    this.model.wallboardFontFamily = this.displaySnapshot.wallboardFontFamily;
    this.customBackgroundColor = this.displaySnapshot.customBackgroundColor;
    this.customTextColor = this.displaySnapshot.customTextColor;
    this.updateDisplayButtonsState();
    this.displayError = '';
    this.footerTextError = '';
    this.maxRowsError = '';
  }

  resetToDefaultSettings(): void {
    const request: WallboardSettingsUpdateRequest = {
      logoId: this.model.logoId,
      footerText: this.model.footerText,
      maxRows: this.model.maxRows.toString(),
      backgroundColor: this.model.wallboardBackgroundColor,
      textColor: this.model.wallboardTextColor,
      fontFamily: this.model.wallboardFontFamily
    };

    this.realTimeService.SetWallboardDefaultSettings(request).subscribe((m) => {
      this.loadWallboardSettings();
      this.showSuccessMessage(this.i18nextPipe.transform('realtime:shared_err_ok'));
      this.notifyParentOfSave();
    });

  }

  // Method to apply predefined color schemes
  applyColorScheme(scheme: keyof typeof this.colorSchemes): void {
    const selectedScheme = this.colorSchemes[scheme];
    this.model.wallboardBackgroundColor = selectedScheme.background;
    this.model.wallboardTextColor = selectedScheme.text;
    this.customBackgroundColor = selectedScheme.background;
    this.customTextColor = selectedScheme.text;
    this.onDisplaySettingsChange();
    console.log(`🎨 Applied color scheme: ${selectedScheme.name}`);
  }


  private takeDisplaySnapshot(): void {
    this.displaySnapshot = {
      logoId: this.model.logoId,
      footerText: this.model.footerText,
      maxRows: this.model.maxRows,
      wallboardBackgroundColor: this.model.wallboardBackgroundColor,
      wallboardTextColor: this.model.wallboardTextColor,
      wallboardFontFamily: this.model.wallboardFontFamily,
      customBackgroundColor: this.customBackgroundColor,
      customTextColor: this.customTextColor
    };
  }

  // Utility methods
  isQueueSelected(queue: QueueItem): boolean {
    return this.selectedQueues.includes(queue.id);
  }

  isColumnSelected(column: ColumnItem): boolean {
    return this.selectedColumns.includes(column.item1);
  }

  isAgentSelected(agent: AgentItem): boolean {
    return this.selectedAgents.includes(agent.userId);
  }

  isAgentColumnSelected(column: ColumnItem): boolean {
    return this.selectedAgentColumns.includes(column.item1);
  }

  private updateSortedLists(): void {
    this.updateSortedQueues();
    this.updateSortedColumns();
    this.updateSortedAgents();
    this.updateSortedAgentColumns();
  }

  private showSuccessMessage(message: string): void {
    // This would typically show a toast notification
    console.log(`✅ ${message}`);

    // In a real application, you would use a toast service like:
    // this.toastService.success(message, 'Success', { duration: 3000 });

    // For now, we'll simulate a brief visual feedback
    this.showTemporarySuccess(message);
  }

  private showTemporarySuccess(message: string): void {
    // Create a temporary success indicator
    const successDiv = document.createElement('div');
    successDiv.className = 'success-message';
    successDiv.textContent = message;
    successDiv.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #4caf50;
      color: white;
      padding: 12px 20px;
      border-radius: 4px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.2);
      z-index: 9999;
      font-size: 14px;
      font-weight: 500;
    `;

    document.body.appendChild(successDiv);

    // Remove after 3 seconds
    setTimeout(() => {
      if (successDiv.parentNode) {
        successDiv.parentNode.removeChild(successDiv);
      }
    }, 3000);
  }

  closeDialog(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    } else {
      this.close.emit();
    }
  }

  private notifyParentOfSave(): void {
    // Notify parent component that settings were saved
    if (this.dialogData && this.dialogData.onSettingsSaved) {
      this.dialogData.onSettingsSaved();
    } else {
      this.settingsSaved.emit();
    }
  }
}
